import { z } from 'zod';

/**
 * Kiosktoestellen (Stap 40, KIO-1, ADR-0076).
 *
 * Een tablet in de kantine wordt een kiosk: de beheerder maakt hem aan en krijgt een code, het
 * toestel typt die in en krijgt een eigen token (hetzelfde patroon als de schermen, ADR-0063). Een
 * medewerker meldt zich aan met personeelsnummer en PIN - of met een badge, die het
 * personeelsnummer intypt - voor hoogstens vijf minuten, en kan alleen lezen en bevestigen.
 */

/** De header waarin het toestel zijn token meestuurt. */
export const KIOSK_TOKEN_HEADER = 'x-kiosk-token';
export const KIOSK_SESSION_SECONDS = 300;

export const kioskStatusSchema = z.enum(['pending', 'active', 'revoked']);
export type KioskStatus = z.infer<typeof kioskStatusSchema>;

export const kioskDeviceSchema = z.object({
  id: z.guid(),
  name: z.string(),
  siteId: z.guid().nullable(),
  siteName: z.string().nullable(),
  status: kioskStatusSchema,
  /** Alleen meteen na aanmaken of opnieuw inrichten; daarna nooit meer. */
  pairingCode: z.string().nullable(),
  pairingExpiresAt: z.string().datetime().nullable(),
  lastSeenAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type KioskDevice = z.infer<typeof kioskDeviceSchema>;

export const kioskDeviceListSchema = z.object({ items: z.array(kioskDeviceSchema) });
export type KioskDeviceList = z.infer<typeof kioskDeviceListSchema>;

export const createKioskRequestSchema = z.object({
  name: z.string().trim().min(2, 'Geef de kiosk een naam.').max(80),
  siteId: z.guid().nullable().default(null),
});
export type CreateKioskRequest = z.infer<typeof createKioskRequestSchema>;

export const updateKioskRequestSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  siteId: z.guid().nullable().optional(),
  /** Een nieuwe code om (opnieuw) in te richten; het oude token werkt daarna niet meer. */
  newPairingCode: z.boolean().optional(),
});
export type UpdateKioskRequest = z.infer<typeof updateKioskRequestSchema>;

export const kioskRegisterRequestSchema = z.object({
  code: z.string().trim().min(8).max(12),
});
export type KioskRegisterRequest = z.infer<typeof kioskRegisterRequestSchema>;

/** Wat het toestel over zichzelf weet; ook het rustscherm toont dit. Geen persoonsgegevens. */
export const kioskInfoSchema = z.object({
  name: z.string(),
  siteName: z.string().nullable(),
  tenantName: z.string(),
  tenantLogoUrl: z.string().nullable(),
  timezone: z.string(),
});
export type KioskInfo = z.infer<typeof kioskInfoSchema>;

export const kioskRegisterResponseSchema = kioskInfoSchema.extend({
  deviceToken: z.string(),
});
export type KioskRegisterResponse = z.infer<typeof kioskRegisterResponseSchema>;

/** Een badgelezer typt het personeelsnummer; voor de server is het dezelfde invoer. */
export const kioskLoginRequestSchema = z.object({
  employeeNumber: z.string().trim().min(1).max(32),
  pin: z.string().regex(/^\d{6}$/),
});
export type KioskLoginRequest = z.infer<typeof kioskLoginRequestSchema>;

/** Het startscherm krijgt de voornaam, en verder niets over de persoon. */
export const kioskSessionSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int(),
  sessionEndsAt: z.string().datetime(),
  firstName: z.string(),
});
export type KioskSession = z.infer<typeof kioskSessionSchema>;

export const kioskMeSchema = z.object({
  firstName: z.string(),
  sessionEndsAt: z.string().datetime(),
});
export type KioskMe = z.infer<typeof kioskMeSchema>;
